import { z } from 'zod';
import { CalendarDate } from './project.js';
import { ProjectCommitment } from './project-commitment.js';
import { ProjectRole } from './rbac.js';
/**
 * Lời mời vào dự án — APM-ROLE-n.
 *
 * ## Một bảng, hai chiều
 *
 * `initiatedBy` quyết định **ai ký sau cùng**: `pm` thì người được mời ký,
 * `user` thì PM ký (đường đăng ký qua link, APM-ROLE-p). Kết cục hai chiều
 * giống hệt nhau — một membership có vai trò, khoảng ngày, cam kết — nên hai
 * bảng song song sẽ lệch nhau ngay ở lần thêm trường thứ nhất.
 *
 * ## ⚠️ Đây là thoả thuận BAN ĐẦU, không phải cam kết đang chạy
 *
 * Duyệt xong thì vai trò, khoảng ngày và cam kết được **chép** sang
 * `project_membership`, và từ đó **PM chốt cam kết** ở tab Cấu hình. Bản ghi
 * này trở thành bất biến: nó trả lời "hai bên đã thoả thuận gì lúc đầu", không
 * trả lời "hiện người này cam kết bao nhiêu".
 *
 * Giao diện vì thế **không được trộn hai con số vào một ô**: chỗ nào hiện cam
 * kết hiện tại thì đọc membership, chỗ nào hiện "đã mời với điều kiện gì" thì
 * đọc bản ghi này.
 */
export const RequestInitiator = z.enum(['pm', 'user']);
export const MembershipRequestStatus = z.enum(['pending', 'accepted', 'declined', 'withdrawn']);
export const MembershipRequestView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    projectKey: z.string(),
    projectName: z.string(),
    /** Mục tiêu dự án — người được mời cần biết mình đang đồng ý tham gia việc gì. */
    projectDescription: z.string().nullable(),
    projectStartDate: CalendarDate.nullable(),
    projectEndDate: CalendarDate.nullable(),
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    /** Phòng ban và kỹ năng — thứ PM cần để quyết ở chiều `user` (APM-ROLE-p). */
    departmentName: z.string().nullable(),
    skills: z.array(z.string()),
    initiatedBy: RequestInitiator,
    roleId: z.uuid(),
    /** `null` = vai trò tuỳ biến của dự án đó. */
    roleKey: ProjectRole.nullable(),
    roleName: z.string(),
    fromDate: CalendarDate.nullable(),
    toDate: CalendarDate.nullable(),
    commitment: ProjectCommitment.nullable(),
    status: MembershipRequestStatus,
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
    decidedAt: z.iso.datetime().nullable(),
});
/**
 * Một lời mời gửi cho **chính tôi**, tra theo id — APM-INVOPEN.
 *
 * Thông báo mời dẫn thẳng tới đây, nên đây phải trả lời được **mọi** kết cục, không chỉ
 * "đang chờ": đã nhận, đã từ chối, đã thu hồi, hay mình đã vào dự án bằng đường khác.
 * Một trang chỉ biết lời mời đang chờ thì với mọi kết cục còn lại là một trang trống —
 * đúng lỗi khiến người được mời bấm thông báo mà không thấy gì.
 *
 * `invitation: null` = không có lời mời này **cho bạn** (id lạ, gửi người khác, hay ở tổ
 * chức khác): không phân biệt ba trường hợp đó, cùng lý do `respond` trả 404 chứ không 403.
 */
export const MyInvitationLookup = z.object({
    invitation: MembershipRequestView.nullable(),
    /** Đã là thành viên dự án — PM thêm tay trong lúc lời mời còn treo. */
    alreadyMember: z.boolean(),
});
/** PM mời một người vào dự án — chiều `pm`. */
export const InviteMemberInput = z.object({
    userId: z.uuid(),
    /** Vai trò đề xuất, phải thuộc **đúng dự án này**. */
    roleId: z.uuid(),
    fromDate: CalendarDate.nullable().optional(),
    toDate: CalendarDate.nullable().optional(),
    /**
     * Thời lượng dự kiến. `null` = chưa chốt, và nó **không** tính vào năng lực —
     * chưa duyệt thì chưa có gì để tính.
     */
    commitment: ProjectCommitment.nullable().optional(),
});
/**
 * Người được mời trả lời.
 *
 * Từ chối **bắt buộc** có lý do: từ chối im lặng thì PM chỉ biết "chưa ai
 * duyệt", và họ sẽ chờ thay vì tìm người khác.
 */
export const RespondToRequestInput = z
    .object({
    accept: z.boolean(),
    note: z.string().max(500).nullable().optional(),
})
    .refine((value) => value.accept || (value.note ?? '').trim().length > 0, {
    message: 'Từ chối thì phải nói lý do',
    path: ['note'],
});
/**
 * PM chốt một đơn đăng ký qua link — chiều `user`, APM-ROLE-p.
 *
 * ## Vì sao PM sửa được cả ba điều kiện ngay ở bước chốt
 *
 * Thứ người đăng ký gửi là **nguyện vọng**: họ chọn vai trò và thời lượng mà
 * không biết dự án đang thiếu gì, ai đã nhận phần nào, và trần năng lực còn bao
 * nhiêu. Bắt PM chấp nhận nguyên văn rồi vào tab Cấu hình sửa lại là hai bước
 * cho một quyết định — và giữa hai bước đó có một người đang ở trong dự án với
 * điều kiện sai.
 *
 * Bỏ trống một trường = **giữ nguyên** thứ người đăng ký đề nghị. `null` tường
 * minh mới là xoá. Hai thứ đó khác nhau: "tôi không đụng tới" không phải "tôi
 * quyết là không có".
 */
export const FinalizeRequestInput = z
    .object({
    accept: z.boolean(),
    roleId: z.uuid().optional(),
    fromDate: CalendarDate.nullable().optional(),
    toDate: CalendarDate.nullable().optional(),
    commitment: ProjectCommitment.nullable().optional(),
    note: z.string().max(500).nullable().optional(),
})
    .refine((value) => value.accept || (value.note ?? '').trim().length > 0, {
    message: 'Từ chối thì phải nói lý do',
    path: ['note'],
});
//# sourceMappingURL=membership-request.js.map